// Solution page content — one entry per /solutions/:slug route.
// Solutions are problem/outcome-framed and bundle 2-4 features; where the
// slug matches a /features page, the title/keyword/framing are deliberately
// different to avoid competing for the same search intent (no cannibalization).

export const SOLUTIONS = [
  {
    slug: "dispatching",
    icon: "CalendarRange",
    painPoint: "Stop playing phone tag with your crew",
    title: "Get Every Truck to the Right Job, On Time",
    metaTitle: "Field Service Dispatching Solution | ServiceGuru Pro",
    metaDescription: "A dispatching solution that combines live GPS, a drag-and-drop board, and mobile status updates so field service teams stop losing time to scheduling chaos.",
    primaryKeyword: "field service dispatching solution",
    intro: "When emergency calls, no-shows, and last-minute reschedules pile up, a dispatcher needs more than a whiteboard. ServiceGuru Pro combines live GPS, a visual board, and mobile status updates into one dispatching solution.",
    benefits: [
      { title: "See the whole day at a glance", desc: "A single dispatch board replaces the whiteboard, the spreadsheet, and the group text." },
      { title: "Assign by real location", desc: "Live GPS shows which technician is actually closest, not just whose day looks emptiest on paper." },
      { title: "Status updates without a phone call", desc: "Technicians update job status from their phone, so the office always knows what's happening." },
    ],
    faqs: [
      { question: "Does this replace our whiteboard and group text?", answer: "Yes — the dispatch board, live GPS, and mobile status updates are designed to replace exactly that workflow." },
    ],
    relatedFeatures: ["dispatching", "gps-fleet-tracking", "scheduling"],
    relatedIndustries: ["hvac", "plumbing"],
  },
  {
    slug: "field-service-management",
    icon: "Wrench",
    painPoint: "One system instead of five",
    title: "Field Service Management, End to End",
    metaTitle: "Field Service Management Solution | ServiceGuru Pro",
    metaDescription: "A complete field service management solution — scheduling, dispatch, mobile app, and GPS tracking — replacing separate tools with one connected platform.",
    primaryKeyword: "field service management solution",
    intro: "Most field service businesses stitch together a scheduling app, a separate GPS tool, and a messaging app just to run a normal day. ServiceGuru Pro replaces the stitching with one connected system.",
    benefits: [
      { title: "Scheduling and dispatch together", desc: "Appointments and the live dispatch board share the same data, so nothing is ever out of sync." },
      { title: "A real mobile app for the field", desc: "Technicians get their full day, job details, and GPS clock-in from one installable app." },
      { title: "Built to scale with your team", desc: "Works the same way for a solo operator and a dispatch team managing dozens of technicians." },
    ],
    faqs: [
      { question: "What does field service management software include?", answer: "It typically covers scheduling, dispatching, mobile access for technicians, and location tracking — all included in ServiceGuru Pro's core platform." },
    ],
    relatedFeatures: ["job-management", "mobile-app", "gps-fleet-tracking", "scheduling"],
    relatedIndustries: ["home-service", "general-contractors"],
  },
  {
    slug: "contractor-crm",
    icon: "Users",
    painPoint: "Stop losing customer history to old texts",
    title: "One Source of Truth for Every Customer",
    metaTitle: "Contractor CRM Solution | ServiceGuru Pro",
    metaDescription: "A contractor CRM solution that keeps customer history, jobs, and sales opportunities together — so nothing lives only in someone's personal phone.",
    primaryKeyword: "contractor CRM solution",
    intro: "When customer history lives in a technician's personal phone or a dispatcher's memory, it disappears the day they're out sick. ServiceGuru Pro keeps it in one shared, searchable record.",
    benefits: [
      { title: "Nothing tied to one person", desc: "Customer history stays with the business even if a technician or dispatcher is out or leaves." },
      { title: "Sales opportunities tracked, not forgotten", desc: "A built-in pipeline means a quoted job never quietly disappears." },
      { title: "One search bar for everything", desc: "Find a customer by name, phone, or address in seconds from the office or the field." },
    ],
    faqs: [
      { question: "What happens to customer history if a technician leaves?", answer: "Nothing is lost — all customer and job history lives in the shared CRM record, not on an individual's device." },
    ],
    relatedFeatures: ["crm", "customer-management", "sales-pipeline"],
    relatedIndustries: ["hvac", "plumbing"],
  },
  {
    slug: "ai-for-contractors",
    icon: "Bot",
    painPoint: "AI that actually answers the phone",
    title: "AI for Contractors, Built for Real Field Work",
    metaTitle: "AI Solutions for Contractors | ServiceGuru Pro",
    metaDescription: "AI for contractors that actually does the work: answers calls, follows up on estimates, diagnoses equipment problems, and takes voice commands hands-free.",
    primaryKeyword: "AI solutions for contractors",
    intro: "Most 'AI for contractors' is a chatbot widget nobody uses. ServiceGuru Pro's AI answers real phone calls, follows up on real estimates, and helps diagnose real equipment problems.",
    benefits: [
      { title: "Answers calls, not just chats", desc: "The AI receptionist picks up real phone calls and books real appointments, 24/7." },
      { title: "Follows up so your team doesn't have to", desc: "Outbound AI calling chases open estimates and overdue invoices automatically." },
      { title: "Helps in front of the equipment", desc: "The diagnostic assistant gives technicians manual-grounded answers on-site." },
      { title: "Hands-free by voice", desc: "Create tasks, book jobs, and pull up info by talking instead of typing." },
    ],
    faqs: [
      { question: "Does ServiceGuru Pro include AI?", answer: "Yes — an AI voice assistant, AI diagnostic assistant, AI receptionist, and outbound AI calling agents are all included." },
    ],
    relatedFeatures: ["ai-receptionist", "ai-voice-agents", "ai-diagnostic-assistant", "ai-assistant"],
    relatedIndustries: ["hvac", "home-service"],
  },
  {
    slug: "ai-receptionist",
    icon: "PhoneIncoming",
    painPoint: "Stop losing jobs to missed calls",
    title: "Never Miss Another Customer Call",
    metaTitle: "AI Receptionist Solution for Missed Calls | ServiceGuru Pro",
    metaDescription: "Solve the missed-call problem for good — ServiceGuru Pro's AI receptionist answers every call and books real appointments, so no lead goes to a competitor.",
    primaryKeyword: "solution for missed contractor calls",
    intro: "A missed call after hours is a lead your competitor answers instead. ServiceGuru Pro's AI receptionist makes sure that never happens — every call gets answered, every time.",
    benefits: [
      { title: "24/7 coverage without night shifts", desc: "Calls get answered around the clock without staffing an overnight desk." },
      { title: "Real appointments, not voicemail", desc: "The AI checks live availability and books directly onto your schedule." },
      { title: "Recognizes repeat customers", desc: "Existing customers are matched to their record for a personal conversation." },
    ],
    faqs: [
      { question: "How does this stop us losing calls to competitors?", answer: "Every call is answered instantly instead of going to voicemail, so the customer never has a reason to call the next business on their list." },
    ],
    relatedFeatures: ["ai-receptionist", "shared-inbox"],
    relatedIndustries: ["plumbing", "home-service"],
  },
  {
    slug: "marketing-automation",
    icon: "Megaphone",
    painPoint: "Win back customers automatically",
    title: "Fill Your Schedule Without Lifting a Finger",
    metaTitle: "Contractor Marketing Automation Solution | ServiceGuru Pro",
    metaDescription: "Automatically win back past customers and collect more reviews with a marketing automation solution built specifically for contractors and home service teams.",
    primaryKeyword: "contractor marketing automation solution",
    intro: "Most past customers never hear from a contractor again after the invoice is paid. ServiceGuru Pro's automation keeps the relationship going without anyone remembering to follow up.",
    benefits: [
      { title: "Reviews collected automatically", desc: "A review request goes out the moment a job is marked complete." },
      { title: "Win-back campaigns", desc: "Automatically reach customers who haven't booked in months." },
      { title: "Personalized at scale", desc: "Merge tags pull in real job and invoice details so messages don't feel like spam." },
    ],
    faqs: [
      { question: "Do I have to remember to send review requests myself?", answer: "No, review requests can be triggered automatically after every completed job." },
    ],
    relatedFeatures: ["marketing-automation", "automations", "shared-inbox"],
    relatedIndustries: ["home-service", "landscaping"],
  },
  {
    slug: "customer-management",
    icon: "Users",
    painPoint: "Every detail, one place",
    title: "One Source of Truth for Every Customer, Job, and Invoice",
    metaTitle: "Customer Management Solution for Contractors | ServiceGuru Pro",
    metaDescription: "Stop hunting across spreadsheets and text threads — a customer management solution that keeps history, equipment, and billing together for every account.",
    primaryKeyword: "customer management solution for contractors",
    intro: "When customer details are scattered across a spreadsheet, a text thread, and someone's memory, service quality suffers. ServiceGuru Pro consolidates it all into one record per customer.",
    benefits: [
      { title: "No more digging for history", desc: "Equipment, past jobs, notes, and billing all live on one profile." },
      { title: "Consistent service every visit", desc: "Any technician can pick up where the last visit left off." },
      { title: "Faster office answers", desc: "Office staff can answer a customer's question without transferring the call." },
    ],
    faqs: [
      { question: "Will this replace our spreadsheet of customers?", answer: "Yes, that's exactly the workflow this is designed to replace." },
    ],
    relatedFeatures: ["customer-management", "crm"],
    relatedIndustries: ["hvac", "pool-service"],
  },
  {
    slug: "job-management",
    icon: "FileText",
    painPoint: "From first call to final invoice",
    title: "Manage Every Job in One Place, Start to Finish",
    metaTitle: "Job Management Solution for Contractors | ServiceGuru Pro",
    metaDescription: "A job management solution that connects the first phone call to the final invoice, so nothing gets lost between the office and the field.",
    primaryKeyword: "job management solution for contractors",
    intro: "A job that starts as a phone call and ends as an invoice touches a lot of hands in between. ServiceGuru Pro keeps every one of those steps connected, not scattered across tools.",
    benefits: [
      { title: "No re-typing between steps", desc: "A job flows into an estimate and an invoice without duplicate data entry." },
      { title: "Full accountability", desc: "Every status change, note, and photo is timestamped and attached to the job." },
      { title: "Office and field always in sync", desc: "A status update from the field shows up on the office dispatch board instantly." },
    ],
    faqs: [
      { question: "Does a job automatically become an invoice?", answer: "Yes, a completed job (or accepted estimate) can convert directly into an invoice without re-entering line items." },
    ],
    relatedFeatures: ["job-management", "estimates", "invoicing"],
    relatedIndustries: ["general-contractors", "roofing"],
  },
  {
    slug: "technician-management",
    icon: "HardHat",
    painPoint: "Know where your crew is and what they earned",
    title: "Technician Management Without the Guesswork",
    metaTitle: "Technician Management Software | ServiceGuru Pro",
    metaDescription: "Technician management software combining live GPS location, GPS-verified time tracking, and flexible payroll — so you always know where your crew is and what they're owed.",
    primaryKeyword: "technician management software",
    intro: "Managing a field crew means knowing where everyone is, whether their hours are accurate, and getting payroll right — without three separate systems that don't talk to each other.",
    benefits: [
      { title: "Live location, always", desc: "See every technician's real-time location alongside their assigned jobs." },
      { title: "GPS-verified hours", desc: "Clock-in and clock-out times are checked against GPS location automatically." },
      { title: "Payroll that matches reality", desc: "Hourly, commission, and piece-rate pay all calculate from the same verified data." },
    ],
    faqs: [
      { question: "Can I see where my technicians are right now?", answer: "Yes, live GPS location is shown for every technician alongside their assigned jobs." },
    ],
    relatedFeatures: ["gps-fleet-tracking", "time-tracking", "payroll"],
    relatedIndustries: ["landscaping", "general-contractors"],
  },
];

export const SOLUTIONS_BY_SLUG = Object.fromEntries(SOLUTIONS.map((s) => [s.slug, s]));
